// Auth client for sutrumai.com. Same-origin against the /api/auth/* routes
// on the DeepAI Lambda. Session token persists in localStorage; the
// AuthContext re-validates it on every reload via /auth/me.

const API_BASE = (process.env.REACT_APP_API_BASE || '').replace(/\/$/, '');
const TOKEN_KEY = 'arthum.session.v1';

export const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
export const FACEBOOK_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID || '';

export function getToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

export function setToken(t) {
  try {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {}
}

async function jsonFetch(path, opts = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
  });
  let data = null;
  try { data = await res.json(); } catch {}
  if (!res.ok) {
    const err = new Error(data?.error?.message || `HTTP ${res.status}`);
    err.status = res.status;
    err.code = data?.error?.code;
    throw err;
  }
  return data;
}

export async function whoAmI() {
  const token = getToken();
  if (!token) return null;
  try {
    const data = await jsonFetch('/api/auth/me', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return data.user || null;
  } catch (err) {
    if (err.status === 401) setToken(null);
    return null;
  }
}

export async function signup({ email, password, name }) {
  const data = await jsonFetch('/api/auth/signup', {
    method: 'POST',
    body: JSON.stringify({ email, password, name }),
  });
  setToken(data.token);
  return data.user;
}

export async function login({ email, password }) {
  const data = await jsonFetch('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  setToken(data.token);
  return data.user;
}

export async function requestOtp({ email }) {
  return await jsonFetch('/api/auth/otp/request', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function verifyOtp({ email, code, name }) {
  const data = await jsonFetch('/api/auth/otp/verify', {
    method: 'POST',
    body: JSON.stringify({ email, code, name }),
  });
  setToken(data.token);
  return data.user;
}

export async function loginWithGoogle({ idToken }) {
  const data = await jsonFetch('/api/auth/google', {
    method: 'POST',
    body: JSON.stringify({ idToken }),
  });
  setToken(data.token);
  return data.user;
}

export async function loginWithFacebook({ accessToken }) {
  const data = await jsonFetch('/api/auth/facebook', {
    method: 'POST',
    body: JSON.stringify({ accessToken }),
  });
  setToken(data.token);
  return data.user;
}

export async function logout() {
  const token = getToken();
  if (token) {
    try {
      await fetch(`${API_BASE}/api/auth/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {}
  }
  setToken(null);
}

// Lazy FB SDK loader. Resolves once window.FB is ready.
let fbReadyPromise = null;
export function loadFacebookSdk() {
  if (fbReadyPromise) return fbReadyPromise;
  if (!FACEBOOK_APP_ID) {
    return Promise.reject(new Error('Facebook login is not configured (missing REACT_APP_FACEBOOK_APP_ID).'));
  }
  fbReadyPromise = new Promise((resolve, reject) => {
    if (window.FB) return resolve(window.FB);
    window.fbAsyncInit = function () {
      try {
        window.FB.init({ appId: FACEBOOK_APP_ID, cookie: false, xfbml: false, version: 'v18.0' });
        resolve(window.FB);
      } catch (err) { reject(err); }
    };
    const s = document.createElement('script');
    s.src = 'https://connect.facebook.net/en_US/sdk.js';
    s.async = true; s.defer = true; s.crossOrigin = 'anonymous';
    s.onerror = () => reject(new Error('Failed to load Facebook SDK.'));
    document.body.appendChild(s);
  });
  return fbReadyPromise;
}

export async function fbLoginPopup() {
  const FB = await loadFacebookSdk();
  return new Promise((resolve, reject) => {
    FB.login(
      (resp) => {
        if (resp?.authResponse?.accessToken) resolve(resp.authResponse.accessToken);
        else reject(new Error('Facebook login was cancelled.'));
      },
      { scope: 'email,public_profile' }
    );
  });
}
